import * as React from 'react'

import { Button } from '~ui/components/buttons/Button/Button'
import { ButtonLarge } from '~ui/components/buttons/ButtonSizes/ButtonLarge'

type TButtonProps = OmitSafe<
  PropsOf<typeof Button>,
  'borderContrast' | 'size' | 'shadow' | 'variant' | 'onBlockedClick' | 'onClick' | 'state'
>

type TButtonOnClick = Defined<PropsOf<typeof Button>['onClick']>

type TProps = RequiredChildren &
  TButtonProps & {
    onClick: () => void
    isBlocked?: boolean
    isLoading?: boolean
  }

/**
 * Submit button of a form.
 */
export const FormSubmitButton: React.FC<TProps> = ({
  children,
  radius,
  bgColor = `primary`,
  onClick,
  isBlocked,
  isLoading,
  ...buttonProps
}) => {
  // prevent default (native) form submit
  const onButtonClick = React.useCallback<TButtonOnClick>(
    (event) => {
      event.preventDefault()
      onClick()
    },
    [onClick],
  )

  const state = React.useMemo<PropsOf<typeof Button>['state']>(() => {
    if (isLoading) return `loading`
    if (isBlocked) return `blocked`

    return `enabled`
  }, [isBlocked, isLoading])

  return (
    <ButtonLarge
      {...buttonProps}
      state={state}
      type={`submit`}
      shadow={`light`}
      variant={`solid`}
      bgColor={bgColor}
      radius={radius}
      onClick={onButtonClick}
      onBlockedClick={onButtonClick}
      textProps={{ fontStyle: `boldSemi`, contrast: bgColor === `primary` ? `normal` : `low` }}
    >
      {children}
    </ButtonLarge>
  )
}
